(() => {
  if (customElements.get('gs-buy-box')) return;

  const decode = (value) => {
    if (typeof value === 'string') {
      if (!value.includes('&')) return value;
      const area = document.createElement('textarea');
      area.innerHTML = value;
      return area.value;
    }
    if (value && typeof value === 'object') {
      Object.keys(value).forEach((key) => { value[key] = decode(value[key]); });
    }
    return value;
  };

  const formatMoney = (cents, format) => {
    const value = typeof cents === 'string' ? cents.replace('.', '') : cents;
    const fmt = format || '€{{amount_with_comma_separator}}';
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const withDelimiters = (number, precision, thousands, decimal) => {
      if (isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? whole + decimal + parts[1] : whole;
    };
    const match = fmt.match(pattern);
    if (!match) return fmt;
    let amount;
    switch (match[1]) {
      case 'amount': amount = withDelimiters(value, 2, ',', '.'); break;
      case 'amount_no_decimals': amount = withDelimiters(value, 0, ',', '.'); break;
      case 'amount_with_comma_separator': amount = withDelimiters(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': amount = withDelimiters(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': amount = withDelimiters(value, 2, "'", '.'); break;
      case 'amount_with_space_separator': amount = withDelimiters(value, 2, ' ', ','); break;
      default: amount = withDelimiters(value, 2, ',', '.');
    }
    return fmt.replace(pattern, amount);
  };

  const lower = (value) => String(value == null ? '' : value).trim().toLowerCase();
  const isBone = (value) => lower(value).includes('bone');
  const FINISH_KEYS = { 'rose gold': 'rose-gold', 'rosegold': 'rose-gold', 'palladium': 'palladium', 'silver': 'palladium', 'black': 'black', 'gold': 'gold' };

  class TagFields {
    constructor(root, host) {
      this.root = root;
      this.host = host;
      this.front = root.querySelector('[data-gs-front]');
      this.back = root.querySelector('[data-gs-back]');
      this.frontField = root.querySelector('[data-gs-front-field]');
      this.count = root.querySelector('[data-gs-front-count]');
      this.error = root.querySelector('[data-gs-front-error]');
      this.shapeButtons = Array.from(root.querySelectorAll('[data-gs-shape]'));
      this.shapeValue = root.querySelector('[data-gs-shape-value]');
      this.preview = root.querySelector('.gs-bb__preview');
      this.previewFront = root.querySelector('[data-gs-preview-front]');
      this.previewBack = root.querySelector('[data-gs-preview-back]');
      this.defaultFront = this.previewFront ? this.previewFront.textContent : '';
      this.defaultBack = this.previewBack ? this.previewBack.textContent : '';
      this.max = 12;

      this.shapeButtons.forEach((button) => {
        button.addEventListener('click', () => this.host.setShape(button.dataset.gsShape));
      });
      if (this.front) {
        this.front.addEventListener('input', () => {
          this.clearError();
          this.host.setEngraving(this.front.value, this.back ? this.back.value : '', this);
        });
      }
      if (this.back) {
        this.back.addEventListener('input', () => {
          this.host.setEngraving(this.front ? this.front.value : '', this.back.value, this);
        });
      }
    }

    sync({ shape, front, back, max, phoneMax, finish }) {
      if (shape != null) {
        this.root.dataset.shape = isBone(shape) ? 'bone' : 'heart';
        this.shapeButtons.forEach((button) => {
          const on = button.dataset.gsShape === shape;
          button.classList.toggle('is-on', on);
          button.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        if (this.shapeValue) this.shapeValue.textContent = shape;
      }
      if (max) this.max = max;
      if (this.front) {
        this.front.maxLength = this.max;
        if (front != null && this.front.value !== front.slice(0, this.max)) this.front.value = front.slice(0, this.max);
        if (this.front.value.length > this.max) this.front.value = this.front.value.slice(0, this.max);
      }
      if (this.back) {
        if (phoneMax) this.back.maxLength = phoneMax;
        if (back != null && this.back.value !== back) this.back.value = back;
      }
      if (this.count && this.front) this.count.textContent = `${this.front.value.length}/${this.max}`;
      if (this.previewFront) this.previewFront.textContent = (this.front && this.front.value.trim()) || this.defaultFront;
      if (this.previewBack) this.previewBack.textContent = (this.back && this.back.value.trim()) || this.defaultBack;
      if (this.preview && finish) this.preview.dataset.finish = finish;
      this.fit();
      requestAnimationFrame(() => this.fit());
    }

    fit() {
      const shrink = (node, max, min) => {
        if (!node || !node.offsetParent) return;
        let size = max;
        node.style.fontSize = `${size}px`;
        while (node.scrollWidth > node.clientWidth && size > min) {
          size -= 0.5;
          node.style.fontSize = `${size}px`;
        }
      };
      shrink(this.previewFront, 17, 7);
      shrink(this.previewBack, 11, 6);
    }

    showError() {
      if (this.error) this.error.hidden = false;
      if (this.frontField) this.frontField.classList.add('is-error');
    }

    clearError() {
      if (this.error) this.error.hidden = true;
      if (this.frontField) this.frontField.classList.remove('is-error');
    }
  }

  class GsBuyBox extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const dataNode = this.querySelector('[data-gs-data]');
      if (!dataNode) return;
      this.ready = true;
      this.data = JSON.parse(dataNode.textContent);
      this.text = decode(this.data.text || {});

      this.prepareSets();
      this.prepareTag();

      this.state = {
        size: null,
        tier: 'none',
        len: 0,
        tag: false,
        shape: this.shapes[0] || null,
        front: '',
        back: '',
        dtier: 'none',
        dlen: 0,
        opt2: null
      };

      this.cacheElements();
      if (this.opt2Buttons.length) this.state.opt2 = this.opt2Buttons[0].dataset.gsOpt2;
      this.bindEvents();
      this.portal([this.drawer, this.sticky]);
      this.hideFinderTrigger();
      this.preselect();
      if (!this.state.size && this.data.autoSelect && this.data.collar.length) {
        const first = this.data.collar.find((v) => v.available) || this.data.collar[0];
        this.state.size = first.options[0];
      }
      this.render();
      this.openFromHash();
    }

    openFromHash() {
      if (!this.drawer || this.dataset.main !== 'true' || window.location.hash !== '#gs-sets') return;
      if (window.history.replaceState) window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
      setTimeout(() => this.openDrawer(), 250);
    }

    disconnectedCallback() {
      setTimeout(() => {
        if (!this.isConnected) (this.portaled || []).forEach((node) => node.remove());
      }, 0);
    }

    portal(nodes) {
      this.portaled = nodes.filter(Boolean);
      const vars = this.getAttribute('style') || '';
      this.portaled.forEach((node) => {
        node.classList.add(...this.classList);
        node.setAttribute('style', `${node.getAttribute('style') || ''}${vars}`);
        document.body.appendChild(node);
      });
    }

    prepareSets() {
      const collarSizes = this.data.collar.map((v) => v.options[0]);
      this.sets = {};
      this.lengths = [];
      (this.data.sets || []).forEach((set) => {
        const counts = set.options.map((_, index) => set.variants.filter((v) => collarSizes.includes(v.options[index])).length);
        const sizeIndex = counts.indexOf(Math.max(...counts));
        const lenIndex = set.options.length > 1 ? set.options.findIndex((_, index) => index !== sizeIndex) : -1;
        const lengths = [];
        if (lenIndex > -1) {
          set.variants.forEach((v) => {
            if (!lengths.includes(v.options[lenIndex])) lengths.push(v.options[lenIndex]);
          });
        }
        this.sets[set.tier] = Object.assign({}, set, { sizeIndex, lenIndex, lengths });
        if (lengths.length > this.lengths.length) this.lengths = lengths;
      });
    }

    prepareTag() {
      const tag = this.data.tag;
      this.shapes = [];
      this.smallSizes = [];
      if (!tag) return;
      const find = (name, fallback) => {
        const index = tag.options.findIndex((option) => lower(option) === name);
        return index > -1 ? index : fallback;
      };
      tag.finishIndex = find('finish', 0);
      tag.shapeIndex = find('shape', 1);
      tag.sizeIndex = find('size', 2);
      tag.variants.forEach((v) => {
        const shape = v.options[tag.shapeIndex];
        if (shape && !this.shapes.includes(shape)) this.shapes.push(shape);
      });
      this.shapes.sort((a, b) => (isBone(a) ? 1 : 0) - (isBone(b) ? 1 : 0));
      this.smallSizes = String(this.data.smallTagSizes || '').split(',').map(lower).filter(Boolean);
      const sizeValues = [];
      tag.variants.forEach((v) => {
        const value = v.options[tag.sizeIndex];
        if (value && !sizeValues.includes(value)) sizeValues.push(value);
      });
      this.tagSmall = sizeValues.find((value) => lower(value).startsWith('s')) || sizeValues[0];
      this.tagLarge = sizeValues.find((value) => lower(value).startsWith('l')) || sizeValues[sizeValues.length - 1];
    }

    cacheElements() {
      const q = (selector) => this.querySelector(selector);
      this.sizeButtons = Array.from(this.querySelectorAll('[data-gs-size]'));
      this.opt2Buttons = Array.from(this.querySelectorAll('[data-gs-opt2]'));
      this.opt2Value = q('[data-gs-opt2-value]');
      this.upgradeLink = q('[data-gs-upgrade-link]');
      this.sizeValue = q('[data-gs-size-value]');
      this.sizeError = q('[data-gs-size-error]');
      this.sizeGroup = q('[data-gs-size-group]');
      this.finder = q('[data-gs-finder]');
      this.setCard = q('[data-gs-set-card]');
      this.tagBlock = q('[data-gs-tag-block]');
      this.tagCard = q('[data-gs-tag-card]');
      this.tagToggle = q('[data-gs-tag-toggle]');
      this.tagImage = q('[data-gs-tag-image]');
      this.tagAdded = q('[data-gs-tag-added]');
      this.tagPriceNode = q('[data-gs-tag-price]');
      this.paidConfig = q('[data-gs-paid-config]');
      this.upgrade = q('[data-gs-upgrade]');
      this.upgradeText = q('[data-gs-upgrade-text]');
      this.upgradeButton = q('[data-gs-upgrade-button]');
      this.form = q('[data-gs-form]');
      this.itemsNode = q('[data-gs-items]');
      this.atc = q('[data-gs-atc]');
      this.totalNode = q('[data-gs-total]');
      this.sticky = q('[data-gs-sticky]');
      this.stickyPrice = q('[data-gs-sticky-price]');
      this.stickyButton = q('[data-gs-sticky-button]');
      this.drawer = q('[data-gs-drawer]');
      this.fields = [];
      const paid = q('[data-gs-paid-config] [data-gs-tag-fields]');
      const free = q('[data-gs-free-config] [data-gs-tag-fields]');
      this.paidFields = paid ? new TagFields(paid, this) : null;
      this.freeFields = free ? new TagFields(free, this) : null;
      [this.paidFields, this.freeFields].forEach((f) => f && this.fields.push(f));
      if (this.drawer) {
        this.panelBody = this.drawer.querySelector('[data-gs-panel-body]');
        this.kicker = this.drawer.querySelector('[data-gs-kicker]');
        this.rows = Array.from(this.drawer.querySelectorAll('[data-gs-row]'));
        this.lengthGroup = this.drawer.querySelector('[data-gs-length-group]');
        this.lengthValue = this.drawer.querySelector('[data-gs-length-value]');
        this.lengthButtons = Array.from(this.drawer.querySelectorAll('[data-gs-length]'));
        this.lengthExtra = this.drawer.querySelector('[data-gs-length-extra]');
        this.freeTagGroup = this.drawer.querySelector('[data-gs-free-tag]');
        this.panelSum = this.drawer.querySelector('[data-gs-panel-sum]');
        this.panelDelta = this.drawer.querySelector('[data-gs-panel-delta]');
        this.confirmButton = this.drawer.querySelector('[data-gs-confirm]');
      }
    }

    bindEvents() {
      this.sizeButtons.forEach((button) => {
        button.addEventListener('click', () => this.selectSize(button.dataset.gsSize));
      });
      this.opt2Buttons.forEach((button) => {
        button.addEventListener('click', () => this.selectOpt2(button.dataset.gsOpt2));
      });
      if (this.finder) this.finder.addEventListener('click', () => this.openFinder());
      document.addEventListener('gs:size-selected', (event) => {
        if (event.detail && event.detail.size) this.selectSize(String(event.detail.size).toUpperCase());
      });
      if (this.setCard) this.setCard.addEventListener('click', () => this.openDrawer());
      if (this.tagToggle) {
        this.tagToggle.addEventListener('change', () => {
          this.state.tag = this.tagToggle.checked;
          this.render();
        });
      }
      if (this.upgradeButton) {
        this.upgradeButton.addEventListener('click', () => {
          this.state.tier = 'signature';
          this.state.tag = false;
          this.render();
        });
      }
      this.addEventListener('submit', this.handleSubmit, true);
      if (this.stickyButton) this.stickyButton.addEventListener('click', () => this.handleSticky());
      if (this.sticky && this.atc && 'IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.sticky.classList.toggle('is-visible', past);
          this.sticky.setAttribute('aria-hidden', past ? 'false' : 'true');
          this.stickyButton.tabIndex = past ? 0 : -1;
        }).observe(this.atc);
      }
      if (this.drawer) {
        this.drawer.querySelectorAll('[data-gs-close]').forEach((button) => button.addEventListener('click', () => this.closeDrawer()));
        this.rows.forEach((row) => {
          const input = row.querySelector('[data-gs-row-input]');
          input.addEventListener('change', () => {
            this.state.dtier = row.dataset.gsRow;
            this.renderDrawer();
          });
        });
        this.lengthButtons.forEach((button) => {
          button.addEventListener('click', () => {
            this.state.dlen = Number(button.dataset.gsLength);
            this.renderDrawer();
          });
        });
        this.confirmButton.addEventListener('click', () => this.confirmDrawer());
        this.drawer.addEventListener('keydown', (event) => this.handleDrawerKeys(event));
      }
    }

    preselect() {
      const param = new URLSearchParams(window.location.search).get('variant');
      if (param) {
        const variant = this.data.collar.find((v) => String(v.id) === param);
        if (variant) {
          this.state.size = variant.options[0];
          if (this.opt2Buttons.length && variant.options[1]) this.state.opt2 = variant.options[1];
          return;
        }
      }
      let remembered = null;
      try {
        remembered = window.sessionStorage.getItem(this.storageKey());
      } catch (error) {
        remembered = null;
      }
      if (remembered && this.collarVariant(remembered)) this.state.size = remembered;
    }

    storageKey() {
      return `gs-bb-size-${lower(this.data.sizeScope || 'default')}`;
    }

    collarVariant(size, opt2) {
      const second = opt2 === undefined ? this.state.opt2 : opt2;
      return this.data.collar.find((v) => v.options[0] === size && (!this.opt2Buttons.length || v.options[1] === second)) || null;
    }

    setVariant(tier, size, len) {
      const set = this.sets[tier];
      if (!set || !size) return null;
      return set.variants.find((v) => v.options[set.sizeIndex] === size && (set.lenIndex < 0 || v.options[set.lenIndex] === set.lengths[len])) || null;
    }

    referenceSize(tier) {
      if (this.state.size && this.setVariant(tier, this.state.size, 0)) return this.state.size;
      const match = this.data.collar.find((v) => this.setVariant(tier, v.options[0], 0));
      return match ? match.options[0] : null;
    }

    delta(tier, len) {
      const size = this.referenceSize(tier);
      const set = this.setVariant(tier, size, len);
      const collar = this.collarVariant(size);
      return set && collar ? set.price - collar.price : null;
    }

    hasSets(size) {
      return Object.keys(this.sets).some((tier) => this.setVariant(tier, size, 0));
    }

    isFree(tier) {
      return Boolean(this.data.tag) && (tier === 'signature' || tier === 'maison') && Boolean(this.sets[tier]);
    }

    tagSizeFor(size) {
      if (!size) return this.tagLarge;
      return this.smallSizes.includes(lower(size)) ? this.tagSmall : this.tagLarge;
    }

    finishKey() {
      return FINISH_KEYS[lower(this.data.hardware)] || 'gold';
    }

    tagVariant(size) {
      const tag = this.data.tag;
      if (!tag) return null;
      const tagSize = this.tagSizeFor(size);
      const shape = this.state.shape;
      const hardware = lower(this.data.hardware);
      const matches = tag.variants.filter((v) => v.options[tag.shapeIndex] === shape && v.options[tag.sizeIndex] === tagSize);
      return matches.find((v) => lower(v.options[tag.finishIndex]) === hardware) || matches[0] || tag.variants[0];
    }

    tagImageFor() {
      const tag = this.data.tag;
      if (!tag) return null;
      if (this.data.tagImage) return this.data.tagImage;
      const hardware = lower(this.data.hardware);
      const withImage = tag.variants.filter((v) => v.image);
      const pick = (list) => list.find((v) => v.options[tag.shapeIndex] === this.state.shape && lower(v.options[tag.finishIndex]) === hardware)
        || list.find((v) => v.options[tag.shapeIndex] === this.state.shape)
        || list[0];
      const variant = pick(withImage);
      if (variant) return variant.image;
      const images = tag.images || [];
      if (!images.length) return null;
      const shape = lower(this.state.shape);
      const finishes = (alt) => {
        const found = [];
        let rest = alt;
        if (rest.includes('rose gold')) {
          found.push('rose gold');
          rest = rest.split('rose gold').join(' ');
        }
        ['palladium', 'silver', 'black', 'gold'].forEach((name) => {
          if (rest.includes(name)) found.push(name === 'silver' ? 'palladium' : name);
        });
        return found;
      };
      const own = hardware === 'silver' ? 'palladium' : hardware;
      const score = (image) => {
        const alt = lower(image.alt);
        const named = finishes(alt);
        let points = alt.includes(shape) ? 2 : 0;
        if (named.includes(own)) points += 3;
        else if (named.length) points -= 3;
        points -= Math.max(0, named.length - 1) * 0.5;
        return points;
      };
      const best = images.reduce((top, image) => (score(image) > score(top) ? image : top), images[0]);
      return best.src;
    }

    nameLimit() {
      const limits = this.data.limits;
      if (!this.state.size) return limits.large;
      return this.tagSizeFor(this.state.size) === this.tagSmall ? limits.small : limits.large;
    }

    money(cents) {
      return formatMoney(cents, this.data.moneyFormat);
    }

    selectSize(size) {
      if (!this.sizeButtons.some((button) => button.dataset.gsSize === size)) return;
      this.state.size = size;
      if (this.state.tier !== 'none' && !this.setVariant(this.state.tier, size, this.state.len)) {
        this.state.tier = 'none';
      }
      if (this.sizeError) this.sizeError.hidden = true;
      if (this.sizeGroup) this.sizeGroup.classList.remove('is-error');
      try {
        window.sessionStorage.setItem(this.storageKey(), size);
      } catch (error) {
        this.storageBlocked = true;
      }
      const variant = this.collarVariant(size);
      if (variant && this.dataset.main === 'true' && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      this.render();
    }

    selectOpt2(value) {
      if (!this.opt2Buttons.some((button) => button.dataset.gsOpt2 === value)) return;
      this.state.opt2 = value;
      const variant = this.state.size ? this.collarVariant(this.state.size) : null;
      if (variant && this.dataset.main === 'true' && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      this.render();
    }

    setShape(shape) {
      this.state.shape = shape;
      this.render();
    }

    setEngraving(front, back, source) {
      this.state.front = front;
      this.state.back = back;
      this.fields.forEach((f) => {
        if (f !== source) f.clearError();
      });
      this.renderFields(source);
      this.renderSetCard();
    }

    findDeep(selector, root = document) {
      const direct = root.querySelector(selector);
      if (direct) return direct;
      const hosts = root.querySelectorAll('*');
      for (const host of hosts) {
        if (host.shadowRoot) {
          const found = this.findDeep(selector, host.shadowRoot);
          if (found) return found;
        }
      }
      return null;
    }

    hideFinderTrigger() {
      const selector = this.data.finderSelector;
      if (!selector || !this.finder) return;
      const hide = 'position:absolute!important;width:1px!important;height:1px!important;margin:0!important;padding:0!important;overflow:hidden!important;border:0!important;white-space:nowrap!important';
      if (!document.getElementById('gs-bb-app-style')) {
        const style = document.createElement('style');
        style.id = 'gs-bb-app-style';
        style.textContent = `.gs-bb-app-hidden{${hide}}`;
        document.head.appendChild(style);
      }
      const apply = () => {
        const trigger = this.findDeep(selector);
        if (!trigger) return false;
        let node = trigger;
        let root = node.getRootNode();
        while (root !== document && root.host) {
          node = root.host;
          root = node.getRootNode();
        }
        const wrapper = node.closest('[data-custom-app], .custom-app-block, .shopify-app-block');
        if (wrapper) {
          wrapper.classList.add('gs-bb-app-hidden');
          return true;
        }
        const target = trigger.getRootNode() === document ? document.head : trigger.getRootNode();
        if (!target.querySelector('style[data-gs-bb-finder]')) {
          const style = document.createElement('style');
          style.setAttribute('data-gs-bb-finder', '');
          style.textContent = `${selector}{${hide};clip:rect(0 0 0 0)!important}`;
          target.appendChild(style);
        }
        return true;
      };
      if (apply()) return;
      let tries = 0;
      const timer = setInterval(() => {
        tries += 1;
        if (apply() || tries > 40) clearInterval(timer);
      }, 250);
    }

    openFinder() {
      document.dispatchEvent(new CustomEvent('gs:open-size-finder', { detail: { productId: this.dataset.productId } }));
      const selector = this.data.finderSelector;
      if (!selector) return;
      const trigger = this.findDeep(selector);
      if (trigger) {
        trigger.dispatchEvent(new MouseEvent('click', { bubbles: true, composed: true, cancelable: true, view: window }));
      }
    }

    render() {
      const { size, tier, tag } = this.state;
      const collar = size ? this.collarVariant(size) : null;

      this.sizeButtons.forEach((button) => {
        const value = button.dataset.gsSize;
        const variant = this.collarVariant(value);
        const on = value === size;
        button.classList.toggle('is-on', on);
        button.classList.toggle('is-soldout', !variant || !variant.available);
        button.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (this.sizeValue) this.sizeValue.textContent = size || this.text.sizePlaceholder || '';
      this.opt2Buttons.forEach((button) => {
        const value = button.dataset.gsOpt2;
        const on = value === this.state.opt2;
        const options = size ? [this.collarVariant(size, value)] : this.data.collar.filter((v) => v.options[1] === value);
        const available = options.some((v) => v && v.available);
        button.classList.toggle('is-on', on);
        button.classList.toggle('is-soldout', !available);
        button.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (this.opt2Value) this.opt2Value.textContent = this.state.opt2 || '';

      if (collar && this.data.priceSelector) {
        const priceNode = document.querySelector(this.data.priceSelector);
        if (priceNode) priceNode.textContent = this.money(collar.price);
      }

      this.renderSetCard();
      this.renderTag();
      this.renderFields();

      const total = this.computeTotal();
      if (this.totalNode) {
        this.totalNode.hidden = total == null;
        this.totalNode.textContent = total == null ? '' : this.money(total);
      }
      if (this.stickyPrice && total != null) this.stickyPrice.textContent = this.money(total);
      if (this.stickyButton) this.stickyButton.textContent = size ? this.text.atc : this.text.chooseSize;
      if (this.atc) {
        const available = !collar || collar.available;
        this.atc.disabled = !available;
      }
    }

    computeTotal() {
      const { size, tier, tag, len } = this.state;
      if (!size) return null;
      const collar = this.collarVariant(size);
      if (!collar) return null;
      let sum = collar.price;
      if (tier !== 'none') {
        const set = this.setVariant(tier, size, len);
        if (set) sum = set.price;
      }
      if (tag && (tier === 'none' || tier === 'essential')) {
        const tv = this.tagVariant(size);
        if (tv) sum += tv.price;
      }
      return sum;
    }

    lengthLabel(len) {
      return this.lengths[len] || '';
    }

    contentsFor(tier, len) {
      const template = (this.text.contents || {})[tier] || '';
      return template.replace(/\[length\]\s*/g, '').replace(/\s{2,}/g, ' ').trim();
    }

    tierTitle(tier) {
      const name = (this.text.tierNames || {})[tier] || tier;
      return this.text.setSuffix ? `${name} ${this.text.setSuffix}` : name;
    }

    renderSetCard() {
      if (!this.setCard) return;
      const { size, tier, len, front } = this.state;
      const title = this.setCard.querySelector('[data-gs-set-card-title]');
      const text = this.setCard.querySelector('[data-gs-set-card-text]');
      const dog = this.setCard.querySelector('[data-gs-set-card-dog]');
      const price = this.setCard.querySelector('[data-gs-set-card-price]');
      const circle = this.setCard.querySelector('.gs-bb__circle');
      const unavailable = Boolean(size) && !this.hasSets(size);
      this.setCard.classList.toggle('is-off', unavailable);
      this.setCard.classList.toggle('is-on', tier !== 'none');
      this.setCard.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
      circle.hidden = unavailable;
      if (unavailable) {
        title.textContent = this.text.setTitle;
        text.textContent = this.text.setUnavailable;
        dog.hidden = true;
        price.hidden = true;
        return;
      }
      price.hidden = false;
      if (tier === 'none') {
        title.textContent = this.text.setTitle;
        text.textContent = this.text.setText;
        dog.hidden = true;
        const firstTier = ['essential', 'signature', 'maison'].find((t) => this.sets[t]);
        const amount = firstTier ? this.delta(firstTier, 0) : null;
        price.textContent = amount == null ? '' : `${this.text.setFrom} + ${this.money(amount)}`;
        return;
      }
      title.textContent = this.tierTitle(tier);
      text.textContent = this.contentsFor(tier, len);
      if (this.isFree(tier)) {
        dog.hidden = false;
        const name = front.trim();
        dog.textContent = name && this.text.tagFor
          ? this.text.tagFor.replace('[shape]', lower(this.state.shape)).replace('[name]', name)
          : this.text.complimentary;
      } else {
        dog.hidden = true;
      }
      const amount = this.delta(tier, len);
      price.textContent = amount == null ? '' : `+ ${this.money(amount)}`;
    }

    renderTag() {
      if (!this.tagBlock) return;
      const { tier, tag } = this.state;
      const visible = tier === 'none' || tier === 'essential';
      this.tagBlock.hidden = !visible;
      if (!visible) return;
      if (this.tagToggle) this.tagToggle.checked = tag;
      this.tagCard.classList.toggle('is-on', tag);
      if (this.tagAdded) this.tagAdded.hidden = !tag;
      const tv = this.tagVariant(this.state.size);
      if (this.tagPriceNode && tv) this.tagPriceNode.textContent = `+ ${this.money(tv.price)}`;
      const image = this.tagImageFor();
      if (this.tagImage && image) {
        if (this.tagImage.getAttribute('src') !== image) this.tagImage.src = image;
        this.tagImage.hidden = false;
      }
      if (this.paidConfig) this.paidConfig.hidden = !tag;
      if (this.upgrade && this.data.upgrade) {
        const current = this.state.size ? this.collarVariant(this.state.size) : null;
        const up = current
          ? (this.data.upgrade.variants.find((v) => v.options.every((o, i) => o === current.options[i]))
            || this.data.upgrade.variants.find((v) => v.options[0] === current.options[0]))
          : null;
        const amount = up && tv ? up.price - current.price - tv.price : null;
        this.upgrade.hidden = !(tag && amount != null && amount > 0);
        if (!this.upgrade.hidden && this.upgradeText) {
          this.upgradeText.innerHTML = this.upgradeText.dataset.template.replace('[amount]', this.money(amount));
        }
        if (this.upgradeLink) this.upgradeLink.href = up ? `${this.data.upgrade.url}?variant=${up.id}` : this.data.upgrade.url;
      } else if (this.upgrade) {
        const show = tier === 'essential' && tag && Boolean(this.sets.signature);
        let amount = null;
        if (show) {
          const sig = this.delta('signature', this.state.len);
          const ess = this.delta('essential', this.state.len);
          if (sig != null && ess != null && tv) amount = sig - ess - tv.price;
        }
        this.upgrade.hidden = !(show && amount != null && amount > 0);
        if (!this.upgrade.hidden && this.upgradeText) {
          this.upgradeText.innerHTML = this.upgradeText.dataset.template.replace('[amount]', this.money(amount));
        }
      }
    }

    renderFields(skip) {
      const payload = {
        shape: this.state.shape,
        front: this.state.front,
        back: this.state.back,
        max: this.nameLimit(),
        phoneMax: this.data.limits.phone,
        finish: this.finishKey()
      };
      this.fields.forEach((f) => {
        if (f === skip) {
          f.sync({ shape: payload.shape, max: payload.max, phoneMax: payload.phoneMax, finish: payload.finish });
        } else {
          f.sync(payload);
        }
      });
    }

    openDrawer() {
      if (!this.drawer || (this.setCard && this.setCard.classList.contains('is-off'))) return;
      this.state.dtier = this.state.tier;
      this.state.dlen = this.state.len;
      this.lastFocus = document.activeElement;
      this.drawer.hidden = false;
      document.documentElement.classList.add('gs-bb-lock');
      requestAnimationFrame(() => this.drawer.classList.add('is-open'));
      this.renderDrawer();
      this.renderFields();
      if (this.panelBody) this.panelBody.scrollTop = 0;
      const close = this.drawer.querySelector('.gs-bb__panel [data-gs-close]');
      if (close) close.focus();
    }

    closeDrawer() {
      if (!this.drawer || this.drawer.hidden) return;
      this.drawer.classList.remove('is-open');
      document.documentElement.classList.remove('gs-bb-lock');
      const done = () => {
        this.drawer.hidden = true;
      };
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) done(); else setTimeout(done, 350);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    }

    handleDrawerKeys(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDrawer();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.drawer.querySelectorAll('.gs-bb__panel button, .gs-bb__panel input, .gs-bb__panel [tabindex]:not([tabindex="-1"])'))
        .filter((node) => !node.disabled && node.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    renderDrawer() {
      if (!this.drawer) return;
      const { size, dtier, dlen } = this.state;
      const colourSource = this.data.colourLabel || this.data.colour || '';
      const colour = colourSource ? colourSource.charAt(0).toUpperCase() + colourSource.slice(1) : '';
      const sizeWord = lower(this.text.sizeWord || '');
      if (this.kicker) this.kicker.textContent = [colour, size ? `${sizeWord} ${size}`.trim() : ''].filter(Boolean).join(', ');

      this.rows.forEach((row) => {
        const tier = row.dataset.gsRow;
        const input = row.querySelector('[data-gs-row-input]');
        const available = tier === 'none' || (size ? Boolean(this.setVariant(tier, size, 0)) : true);
        row.hidden = !available;
        const on = tier === dtier;
        row.classList.toggle('is-on', on);
        input.checked = on;
        const contents = row.querySelector('[data-gs-row-contents]');
        if (contents) contents.textContent = this.contentsFor(tier, dlen);
        const price = row.querySelector('[data-gs-row-price]');
        if (price) {
          const amount = this.delta(tier, dlen);
          price.textContent = amount == null ? '' : `+ ${this.money(amount)}`;
        }
      });

      const showLengths = dtier !== 'none' && this.lengths.length > 1;
      if (this.lengthGroup) this.lengthGroup.hidden = !showLengths;
      if (showLengths) {
        this.lengthButtons.forEach((button) => {
          const index = Number(button.dataset.gsLength);
          const on = index === dlen;
          button.classList.toggle('is-on', on);
          button.setAttribute('aria-pressed', on ? 'true' : 'false');
          const label = button.querySelector('[data-gs-length-label]');
          if (label) label.textContent = this.lengthLabel(index);
        });
        if (this.lengthValue) this.lengthValue.textContent = this.lengthLabel(dlen);
        if (this.lengthExtra) {
          const short = this.delta(dtier, 0);
          const long = this.delta(dtier, 1);
          this.lengthExtra.textContent = short != null && long != null && long > short ? `+ ${this.money(long - short)}` : '';
        }
      }

      if (this.freeTagGroup) this.freeTagGroup.hidden = !this.isFree(dtier);

      const amount = dtier === 'none' ? null : this.delta(dtier, dlen);
      if (this.panelSum) this.panelSum.hidden = amount == null;
      if (this.panelDelta && amount != null) this.panelDelta.textContent = `+ ${this.money(amount)}`;
      if (this.confirmButton) {
        this.confirmButton.textContent = dtier === 'none'
          ? this.text.continueBase
          : (this.text.chooseSet || '').replace('[set]', (this.text.tierNames || {})[dtier] || dtier);
      }
    }

    confirmDrawer() {
      const { dtier, dlen } = this.state;
      if (this.isFree(dtier) && !this.state.front.trim()) {
        if (this.freeFields) {
          this.freeFields.showError();
          this.freeFields.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (this.freeFields.front) this.freeFields.front.focus({ preventScroll: true });
        }
        return;
      }
      this.state.tier = dtier;
      this.state.len = dlen;
      if (this.isFree(dtier)) this.state.tag = false;
      this.closeDrawer();
      this.render();
      if (dtier !== 'none' && this.state.size) {
        if (this.form.requestSubmit) this.form.requestSubmit(this.atc);
        else this.atc.click();
      }
    }

    showSizeError() {
      if (this.sizeError) this.sizeError.hidden = false;
      if (this.sizeGroup) {
        this.sizeGroup.classList.add('is-error');
        this.sizeGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    handleSticky() {
      if (!this.state.size) {
        this.showSizeError();
        return;
      }
      if (this.form.requestSubmit) this.form.requestSubmit(this.atc);
      else this.atc.click();
    }

    buildItems() {
      const { size, tier, len, tag } = this.state;
      const items = [];
      const set = tier !== 'none' ? this.setVariant(tier, size, len) : null;
      if (set) items.push({ id: set.id, quantity: 1, properties: {} });
      else items.push({ id: this.collarVariant(size).id, quantity: 1, properties: {} });
      const free = (Boolean(set) && this.isFree(tier)) || Boolean(this.data.freeTag);
      const paid = !this.data.freeTag && tag && (tier === 'none' || tier === 'essential');
      if (free || paid) {
        const tv = this.tagVariant(size);
        if (tv) {
          const props = {};
          props[this.data.props.front] = this.state.front.trim();
          if (this.state.back.trim()) props[this.data.props.back] = this.state.back.trim();
          if (free) props[this.data.props.set] = set ? this.sets[tier].title : this.data.title;
          items.push({ id: tv.id, quantity: 1, properties: props });
        }
      }
      return items;
    }

    writeItems(items) {
      this.itemsNode.innerHTML = '';
      const add = (name, value) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        this.itemsNode.appendChild(input);
      };
      items.forEach((item, index) => {
        add(`items[${index}][id]`, item.id);
        add(`items[${index}][quantity]`, item.quantity);
        Object.keys(item.properties).forEach((key) => add(`items[${index}][properties][${key}]`, item.properties[key]));
      });
    }

    validate() {
      const { size, tier, tag } = this.state;
      if (!size || !this.collarVariant(size)) {
        this.showSizeError();
        return false;
      }
      const needsName = (tag && (tier === 'none' || tier === 'essential')) || this.isFree(tier) || Boolean(this.data.freeTag);
      if (needsName && !this.state.front.trim()) {
        if (this.data.freeTag && this.freeFields) {
          this.freeFields.showError();
          this.freeFields.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (this.freeFields.front) this.freeFields.front.focus({ preventScroll: true });
        } else if (this.isFree(tier)) {
          this.openDrawer();
          if (this.freeFields) this.freeFields.showError();
        } else if (this.paidFields) {
          this.paidFields.showError();
          this.paidFields.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return false;
      }
      return true;
    }

    handleSubmit = (event) => {
      if (event.target !== this.form) return;
      if (!this.validate()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      const items = this.buildItems();
      this.writeItems(items);
      if (!customElements.get('product-form')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        }).then(() => {
          window.location.href = `${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}cart`;
        });
      }
    };
  }

  class GsTagEngraving extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const dataNode = this.querySelector('[data-gs-data]');
      const root = this.querySelector('[data-gs-tag-fields]');
      if (!dataNode || !root) return;
      this.ready = true;
      this.data = JSON.parse(dataNode.textContent);
      const index = (name, fallback) => {
        const found = this.data.options.findIndex((option) => lower(option) === name);
        return found > -1 ? found : fallback;
      };
      this.finishIndex = index('finish', 0);
      this.shapeIndex = index('shape', 1);
      this.sizeIndex = index('size', 2);
      this.front = '';
      this.back = '';
      this.fields = new TagFields(root, this);
      this.form = document.getElementById(this.dataset.formId);
      this.variantId = this.data.selected;
      if (this.form) {
        const holder = this.form.closest('product-form') || this.form.parentElement;
        holder.addEventListener('submit', this.handleSubmit, true);
        const input = this.form.querySelector('[data-product-form-variant], [name="id"]');
        if (input) {
          this.variantInput = input;
          new MutationObserver(() => this.update()).observe(input, { attributes: true, attributeFilter: ['value'] });
          input.addEventListener('change', () => this.update());
        }
      }
      document.addEventListener('change', (event) => {
        if (event.target && event.target.closest && event.target.closest('variant-picker, .product-variant-picker')) setTimeout(() => this.update(), 50);
      });
      this.update();
    }

    currentVariant() {
      const id = this.variantInput ? this.variantInput.value : this.variantId;
      return this.data.variants.find((v) => String(v.id) === String(id)) || this.data.variants[0];
    }

    update() {
      const variant = this.currentVariant();
      if (!variant) return;
      const size = lower(variant.options[this.sizeIndex]);
      const max = size.startsWith('s') ? this.data.limits.small : this.data.limits.large;
      this.fields.sync({
        shape: variant.options[this.shapeIndex],
        max,
        phoneMax: this.data.limits.phone,
        finish: FINISH_KEYS[lower(variant.options[this.finishIndex])] || 'gold'
      });
    }

    setShape() {}

    setEngraving(front, back) {
      this.front = front;
      this.back = back;
      this.fields.sync({});
    }

    handleSubmit = (event) => {
      if (event.target !== this.form) return;
      const value = this.fields.front ? this.fields.front.value.trim() : '';
      if (!value) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.fields.showError();
        this.fields.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };
  }

  class GsColourSiblings extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const value = this.querySelector('[data-gs-colour-value]');
      if (!value) return;
      const current = value.dataset.current || value.textContent;
      this.querySelectorAll('[data-gs-swatch]').forEach((swatch) => {
        swatch.addEventListener('mouseenter', () => {
          value.textContent = swatch.dataset.gsSwatch;
        });
        swatch.addEventListener('mouseleave', () => {
          value.textContent = current;
        });
        swatch.addEventListener('focus', () => {
          value.textContent = swatch.dataset.gsSwatch;
        });
        swatch.addEventListener('blur', () => {
          value.textContent = current;
        });
      });
    }
  }

  class GsProductDetails extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('[data-gs-pd-finder]');
      if (!button) return;
      const finder = () => document.querySelector('gs-buy-box [data-gs-finder]');
      if (finder()) button.hidden = false;
      button.addEventListener('click', () => {
        const target = finder();
        if (target) target.click();
      });
    }
  }

  class GsCompleteLook extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll('[data-gs-look]'));
      this.panels = Array.from(this.querySelectorAll('[data-gs-panel]'));
      this.buttons.forEach((button) => {
        button.addEventListener('click', () => this.show(button.dataset.gsLook));
      });
    }

    show(look) {
      this.buttons.forEach((button) => {
        const on = button.dataset.gsLook === look;
        button.classList.toggle('is-on', on);
        button.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.gsPanel !== look;
      });
    }
  }

  customElements.define('gs-buy-box', GsBuyBox);
  if (!customElements.get('gs-tag-engraving')) customElements.define('gs-tag-engraving', GsTagEngraving);
  if (!customElements.get('gs-colour-siblings')) customElements.define('gs-colour-siblings', GsColourSiblings);
  if (!customElements.get('gs-product-details')) customElements.define('gs-product-details', GsProductDetails);
  if (!customElements.get('gs-complete-look')) customElements.define('gs-complete-look', GsCompleteLook);
})();
